<template>
  <div :data-view-id="componentUniqueTitle || uniqueTitle">
    <b-container fluid class="maps-container">
      <ViewTitle :title="title" />
      <b-row v-if="visiblePresetFilterButtons.length" class="mb-2">
        <b-col>
          <PresetFilterButtons
            :buttons="visiblePresetFilterButtons"
            :active-ids="activePresetFilterIds"
            @toggle="onPresetFilterToggle"
          />
        </b-col>
      </b-row>
      <b-row v-if="searchEnabled" class="mb-2">
        <b-col>
          <ViewSearch :model-value="searchText" @update:model-value="onSearchInput" />
        </b-col>
      </b-row>
      <b-row v-if="userFiltering" class="mb-3">
        <b-col>
          <RuntimeFilterBuilder
            ref="filterBuilder"
            :field-groups="filterFieldGroups"
            :initial-filters="resolvedInitialFilters"
            :initial-match-mode="resolvedInitialMatchMode"
            @filter-changed="onFilterChanged"
          />
        </b-col>
      </b-row>
      <div ref="mapCanvas" class="map-canvas"></div>
      <div v-if="mapError" class="map-note text-danger">{{ mapError }}</div>
      <div v-else-if="mappedCount === 0" class="map-note text-muted">No records with a location to show.</div>
      <div v-else class="map-note text-muted">{{ mappedCount }} location{{ mappedCount === 1 ? '' : 's' }}</div>
      <details v-if="locatedItems.length" class="map-locations">
        <summary>Locations</summary>
        <ul>
          <li v-for="(item, index) in locatedItems" :key="index">
            <button type="button" @click="showLocation(item)">{{ locationLabel(item) }}</button>
          </li>
        </ul>
      </details>
    </b-container>
  </div>
</template>

<script>
import _ from 'lodash'
import axios from 'axios'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import RuntimeFilterBuilder from './RuntimeFilterBuilder.vue'
import ViewTitle from './ViewTitle.vue'
import ViewSearch from './ViewSearch.vue'
import { displayListValue, formatMultiselectDisplay, readMultiselectItems } from '../lib/multiselect-display'
import { fieldDisplayValue, shownField } from '../../../shared/field-display'
import { realtimeSubscribeFields } from '../lib/view-visibility'
import { liveAppWebSocketUrl } from '../lib/live-app-socket'
import { eventRefreshesView, fieldIndexMap } from '../../../shared/view-refresh'
import { viewSearchParam } from '../../../shared/view-search'
import { formatAppInstant } from '../lib/app-time-zone'
import { filterQueryHref, readFilterQuery } from '../lib/filter-query'
import PresetFilterButtons from './PresetFilterButtons.vue'
import { presetFilterMixin } from '../lib/preset-filter-state'
import { DEFAULT_MAP_STYLE_URL, MAP_ROW_LIMIT, stampMapPoint } from '../../../shared/map-location'
import { imageCellHtml, isViewImageMarkup } from '../../../shared/image-column'
import { runViewLinkCode } from '../lib/run-custom-code'
import { viewLinkHref, viewRecordLink } from '../../../shared/view-fields'

function escapeHtml (value) {

  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export default {
  mixins: [presetFilterMixin],
  components: {
    RuntimeFilterBuilder,
    ViewTitle,
    ViewSearch,
    PresetFilterButtons
  },
  data () {
    return {
      filterFieldGroups: [],
      filteredItems: null,
      urlFilters: null,
      urlMatchMode: null,
      activeFilterEvent: null,
      searchText: '',
      ws: null,
      mapError: '',
      mappedCount: 0
    }
  },
  props: {
    title: { type: String, default: '' },
    uniqueTitle: { type: String, default: '' },
    userFiltering: { type: Boolean },
    realtime: { type: Boolean, default: false },
    spreadSheetUniqueTitle: { type: String, default: '' },
    fields: { type: Array, default: () => [] },
    items: { type: Array, default: () => [] },
    pageUniqueTitle: { type: String, default: '' },
    componentUniqueTitle: { type: String, default: '' },
    fieldCounterMap: { type: Object, default: null },
    defaultUserFilters: { type: Array, default: () => [] },
    defaultUserFilterMatchMode: { type: String, default: 'all' },
    restrictUserFiltering: { type: Boolean, default: false },
    searchEnabled: { type: Boolean, default: false },
    addressColumn: { type: String, default: '' },
    latitudeColumn: { type: String, default: '' },
    longitudeColumn: { type: String, default: '' },
    mapStyleUrl: { type: String, default: '' }
  },
  computed: {
    displayItems () {
      return this.filteredItems !== null ? this.filteredItems : this.items
    },
    locationView () {
      return {
        type: 'maps',
        addressColumn: this.addressColumn,
        latitudeColumn: this.latitudeColumn,
        longitudeColumn: this.longitudeColumn
      }
    },
    styleUrl () {
      return this.mapStyleUrl || DEFAULT_MAP_STYLE_URL
    },
    filterQueryKey () {
      return `f_${this.componentUniqueTitle || this.uniqueTitle}`
    },
    searchQueryKey () {
      return viewSearchParam(this.componentUniqueTitle || this.uniqueTitle)
    },
    locatedItems () {
      return (this.displayItems || []).filter(item => Number.isFinite(Number(item && item._lat)) && Number.isFinite(Number(item && item._lng)))
    },
    searchTerm () {
      return String(this.searchText || '').trim()
    },
    resolvedInitialFilters () {
      if (this.urlFilters) return this.urlFilters
      return this.defaultUserFilters
    },
    resolvedInitialMatchMode () {
      if (this.urlMatchMode) return this.urlMatchMode
      return this.defaultUserFilterMatchMode
    }
  },
  mounted () {
    if (this.userFiltering) {
      this.readFiltersFromUrl()
      this.loadFilterFields()
    }
    if (this.searchEnabled) {
      this.readSearchFromUrl()
      if (this.searchTerm) this.fetchMapData(this.activeFilterEvent, { alwaysApply: true })
    }
    if (this.realtime) this.connectRealtimeWebSocket()
    this._onRefreshViews = (event) => {
      if (!eventRefreshesView(event, this.componentUniqueTitle || this.uniqueTitle)) return
      this.refreshData()
    }
    window.addEventListener('superturbo-refresh-views', this._onRefreshViews)
    this.$nextTick(() => this.mountMap())
  },
  beforeUnmount () {
    clearTimeout(this._searchTimer)
    this.disconnectRealtimeWebSocket()
    if (this._onRefreshViews) window.removeEventListener('superturbo-refresh-views', this._onRefreshViews)
    if (this._map) {
      this._map.remove()
      this._map = null
    }
  },
  watch: {
    displayItems: {
      deep: true,
      handler () {
        this.syncMapData()
      }
    },
    styleUrl () {
      this.mountMap()
    },
    userFiltering (val) {
      if (val && !this.filterFieldGroups.length) this.loadFilterFields()
    }
  },
  methods: {
    locationLabel (item) {
      for (const field of this.fields || []) {
        const value = item[field.key]
        if (value == null || value === '' || isViewImageMarkup(value)) continue
        const raw = item._raw && item._raw[field.key] != null ? item._raw[field.key] : value
        if (readMultiselectItems(raw)) return formatMultiselectDisplay(raw, 'comma')
        return String(value)
      }
      return 'Location'
    },
    showLocation (item) {
      if (!this._map) return
      const lng = Number(item._lng)
      const lat = Number(item._lat)
      this.openPopup(item, [lng, lat])
      this._map.easeTo({ center: [lng, lat], zoom: Math.max(this._map.getZoom(), 12) })
    },
    mountMap () {
      if (!this.$refs.mapCanvas) return
      if (this._map) {
        this._map.remove()
        this._map = null
      }
      this.mapError = ''
      try {
        this._map = new maplibregl.Map({
          container: this.$refs.mapCanvas,
          style: this.styleUrl,
          center: [-98.5, 39.8],
          zoom: 3
        })
      } catch (err) {
        this.mapError = 'The map could not be started.'
        return
      }
      this._map.addControl(new maplibregl.NavigationControl(), 'top-right')
      this._map.on('error', (event) => {
        if (event && event.error && /style|sprite|glyph|tile|ajax/i.test(String(event.error.message || event.error))) {
          this.mapError = 'The map style could not be loaded.'
        }
      })
      this._map.on('load', () => {
        this._map.addSource('records', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 50
        })
        this._map.addLayer({
          id: 'clusters',
          type: 'circle',
          source: 'records',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': '#2563eb',
            'circle-radius': ['step', ['get', 'point_count'], 16, 10, 22, 50, 28],
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff'
          }
        })
        this._map.addLayer({
          id: 'cluster-count',
          type: 'symbol',
          source: 'records',
          filter: ['has', 'point_count'],
          layout: {
            'text-field': ['get', 'point_count_abbreviated'],
            'text-size': 12
          },
          paint: { 'text-color': '#ffffff' }
        })
        this._map.addLayer({
          id: 'points',
          type: 'circle',
          source: 'records',
          filter: ['!', ['has', 'point_count']],
          paint: {
            'circle-color': '#1d4ed8',
            'circle-radius': 7,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff'
          }
        })
        this._map.on('click', 'clusters', (event) => {
          const feature = event.features && event.features[0]
          if (!feature) return
          const source = this._map.getSource('records')
          source.getClusterExpansionZoom(Number(feature.properties.cluster_id)).then((zoom) => {
            this._map.easeTo({ center: feature.geometry.coordinates, zoom })
          }).catch(() => {})
        })
        this._map.on('click', 'points', (event) => {
          const feature = event.features && event.features[0]
          if (!feature) return
          const item = this._featureItems[feature.properties.itemIndex]
          this.openPopup(item, feature.geometry.coordinates)
        })
        this._map.on('mouseenter', 'clusters', () => { this._map.getCanvas().style.cursor = 'pointer' })
        this._map.on('mouseleave', 'clusters', () => { this._map.getCanvas().style.cursor = '' })
        this._map.on('mouseenter', 'points', () => { this._map.getCanvas().style.cursor = 'pointer' })
        this._map.on('mouseleave', 'points', () => { this._map.getCanvas().style.cursor = '' })
        this._mapReady = true
        this.syncMapData(true)
      })
    },
    openPopup (item, lngLat) {

      if (!this._map || !item) return

      const popup = new maplibregl.Popup({ offset: 12 })
        .setLngLat(lngLat)
        .setHTML(this.popupHtml(item))
        .addTo(this._map)
      const root = popup.getElement()

      if (!root) return

      root.querySelectorAll('[data-code-field]').forEach((button) => {

        button.addEventListener('click', (event) => {

          event.preventDefault()
          const field = (this.fields || []).find((entry) => String(entry.key) === button.getAttribute('data-code-field'))

          if (field) runViewLinkCode(field.code, item)
        })
      })
      root.querySelectorAll('[data-link-href]').forEach((anchor) => {

        anchor.addEventListener('click', (event) => {

          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return

          const href = anchor.getAttribute('href')

          if (!href || !this.$router) return

          event.preventDefault()
          this.$router.push(href).catch(() => {})
        })
      })
    },
    fieldLinkHref (item, field) {

      return viewLinkHref(viewRecordLink(item, field), {
        routePath: this.routePath || (this.$route && this.$route.path) || '',
        application: this.$route && this.$route.params ? this.$route.params.application : ''
      })
    },
    popupHtml (item) {
      if (!item) return ''
      const rows = (this.fields || []).map((field) => {
        if (field.linkMode === 'code') {

          const text = field.customText || field.label || 'Run'
          const buttonClass = field.displayAsButton ? 'view-link-button' : 'map-code-link'

          return `<div class="map-popup-field"><button type="button" class="${buttonClass}" data-code-field="${escapeHtml(String(field.key))}">${escapeHtml(text)}</button></div>`
        }

        if (field.type === 'link' && field.displayAsButton) {

          const text = field.customText || item[field.key] || field.label || 'Open'
          const label = field.label ? `<div class="map-popup-label">${escapeHtml(field.label)}</div>` : ''
          const href = this.fieldLinkHref(item, field)

          if (!href) return `<div class="map-popup-field">${label}<span class="view-link-button is-disabled">${escapeHtml(String(text))}</span></div>`

          return `<div class="map-popup-field">${label}<a class="view-link-button" data-link-href="1" href="${escapeHtml(href)}">${escapeHtml(String(text))}</a></div>`
        }

        const value = item[field.key]
        if (value === undefined || value === null || value === '') return ''
        const label = field.label ? `<div class="map-popup-label">${escapeHtml(field.label)}</div>` : ''
        const raw = item._raw && item._raw[field.key] != null ? item._raw[field.key] : value
        const display = shownField(fieldDisplayValue(raw, value), {
          multiselectDisplay: field.multiselectDisplay,
          valueFormatting: field.valueFormatting
        })
        const shown = display ? display.html : (isViewImageMarkup(value) ? String(value) : escapeHtml(value))
        return `<div class="map-popup-field">${label}<div>${shown}</div></div>`
      }).filter(Boolean)
      return rows.join('') || '<div>Record</div>'
    },
    syncMapData (fit) {
      if (!this._map || !this._mapReady) return
      const source = this._map.getSource('records')
      if (!source) return
      const features = []
      this._featureItems = []
      const bounds = new maplibregl.LngLatBounds()
      for (const item of this.displayItems || []) {
        const lat = Number(item && item._lat)
        const lng = Number(item && item._lng)
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue
        const itemIndex = this._featureItems.length
        this._featureItems.push(item)
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [lng, lat] },
          properties: { itemIndex }
        })
        bounds.extend([lng, lat])
      }
      this.mappedCount = features.length
      source.setData({ type: 'FeatureCollection', features })
      if (fit && features.length === 1) {
        this._map.easeTo({ center: features[0].geometry.coordinates, zoom: 12 })
      } else if (fit && features.length > 1) {
        this._map.fitBounds(bounds, { padding: 48, maxZoom: 14 })
      }
      this._map.resize()
    },
    async loadFilterFields () {
      const pageUT = this.pageUniqueTitle || this.routePageUniqueTitle || window.__superturbo_getPageUniqueTitle?.()
      const compUT = this.componentUniqueTitle || this.title
      if (!pageUT || !compUT) return
      const account = this.routeAccount
      const application = this.$route?.params?.application || ''
      try {
        let url = `${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/pages/${pageUT}/components/${compUT}/filterfields`
        if (this.restrictUserFiltering) url += '?restrict=true'
        const response = await axios.get(url)
        this.filterFieldGroups = response.data
      } catch (err) {
        console.warn('Failed to load filter fields:', err)
      }
    },
    compactFilter (filter) {
      const compact = {}
      for (const [key, value] of Object.entries(filter)) {
        if (value === null || value === undefined) continue
        compact[key] = key === 'valueFieldRef' && value !== null ? this.compactFilter(value) : value
      }
      return compact
    },
    expandFilter (compact) {
      const full = {
        relationshipColumn: null,
        relatedSpreadsheet: null,
        sourceSpreadsheet: null,
        sourceRelationshipColumn: null,
        relQuantifier: null,
        valueType: 'literal',
        value: '',
        valueFieldRef: null,
        ...compact
      }
      if (full.valueFieldRef && typeof full.valueFieldRef === 'object') {
        full.valueFieldRef = this.expandFilter(full.valueFieldRef)
      }
      return full
    },
    filtersMatchDefaults (filterEvent) {
      const defaults = this.defaultUserFilters || []
      const current = (filterEvent && filterEvent.filters) || []
      const currentMode = (filterEvent && filterEvent.matchMode) || 'all'
      const defaultMode = this.defaultUserFilterMatchMode || 'all'
      if (currentMode !== defaultMode) return false
      if (current.length !== defaults.length) return false
      return JSON.stringify(current) === JSON.stringify(defaults)
    },
    readFiltersFromUrl () {
      const parsed = readFilterQuery(window.location.search, this.filterQueryKey)
      if (!parsed) return
      this.urlFilters = parsed.filters.map(filter => this.expandFilter(filter))
      this.urlMatchMode = parsed.matchMode
    },
    writeFiltersToUrl (filterEvent) {
      try {
        const hasFilters = filterEvent && filterEvent.filters && filterEvent.filters.length > 0
        let payload = null
        if (hasFilters && !this.filtersMatchDefaults(filterEvent)) {
          payload = { filters: filterEvent.filters.map(filter => this.compactFilter(filter)) }
          if ((filterEvent.matchMode || 'all') !== 'all') payload.matchMode = filterEvent.matchMode
        }
        window.history.replaceState(null, '', filterQueryHref(window.location.href, this.filterQueryKey, payload))
      } catch (err) {
        console.warn('Failed to write filters to URL:', err)
      }
    },
    readSearchFromUrl () {
      try {
        const params = new URLSearchParams(window.location.search)
        this.searchText = params.get(this.searchQueryKey) || ''
      } catch (err) {
        this.searchText = ''
      }
    },
    writeSearchToUrl () {
      try {
        const url = new URL(window.location.href)
        if (this.searchTerm) url.searchParams.set(this.searchQueryKey, this.searchTerm)
        else url.searchParams.delete(this.searchQueryKey)
        window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
      } catch (err) {
        console.warn('Failed to write search to URL:', err)
      }
    },
    onSearchInput (value) {
      this.searchText = value
      clearTimeout(this._searchTimer)
      this._searchTimer = setTimeout(() => {
        this.writeSearchToUrl()
        this.fetchMapData(this.activeFilterEvent)
      }, 300)
    },
    async onFilterChanged (filterEvent) {
      this.writeFiltersToUrl(filterEvent)
      this.activeFilterEvent = filterEvent
      await this.fetchMapData(filterEvent)
    },
    reloadForPresetFilters () {
      return this.fetchMapData(this.activeFilterEvent, { alwaysApply: true })
    },
    async refreshData () {
      if (window.__superturbo_preview_mode) return
      await this.fetchMapData(this.activeFilterEvent, { alwaysApply: true })
    },
    async fetchMapData (filterEvent, { alwaysApply = false } = {}) {
      const hasFilters = filterEvent && filterEvent.filters && filterEvent.filters.length > 0
      const hasSearch = this.searchEnabled && !!this.searchTerm
      const hasPreset = this.activePresetFilterIds.length > 0
      if (!alwaysApply && !hasFilters && !hasSearch && !hasPreset) {
        this.filteredItems = null
        this.syncMapData(true)
        return
      }
      const pageUT = this.pageUniqueTitle || this.routePageUniqueTitle || window.__superturbo_getPageUniqueTitle?.()
      const compUT = this.componentUniqueTitle || this.uniqueTitle
      const account = this.routeAccount
      const application = this.$route?.params?.application || ''
      let url = `${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/pages/${pageUT}/components/${compUT}?limit=${MAP_ROW_LIMIT}`
      if (hasFilters) {
        url += `&userFilters=${encodeURIComponent(JSON.stringify(filterEvent.filters))}`
        url += `&userFilterMatchMode=${encodeURIComponent(filterEvent.matchMode || 'all')}`
      }
      if (hasSearch) url += `&search=${encodeURIComponent(this.searchTerm)}`
      if (this.presetFilterQuery()) url += `&presetFilters=${encodeURIComponent(this.presetFilterQuery())}`
      const accessKey = this.routeAccessKey
      if (accessKey) url += `&accessKey=${encodeURIComponent(accessKey)}`
      const requestId = (this._mapRequestId = (this._mapRequestId || 0) + 1)
      try {
        const response = await axios.get(url)
        if (requestId !== this._mapRequestId) return
        this.filteredItems = this.transformResponseData(response.data)
        this.syncMapData(true)
      } catch (err) {
        if (requestId !== this._mapRequestId) return
        console.warn('Failed to fetch map data:', err)
      }
    },
    transformResponseData (data) {
      const index = fieldIndexMap(this.fieldCounterMap, this.fields)
      if (!index) return data
      return _.map(data, (row) => {
        const newRow = { _values: row }
        if (row._formatting) newRow._formatting = row._formatting
        if (row._links) newRow._links = row._links
        if (row.links) newRow.links = row.links
        if (row.id !== undefined) newRow._rowId = row.id
        for (const key of Object.keys(row)) {
          if (key === '_formatting' || key === '_links' || key === 'links') continue
          if (key.endsWith('_filename') || key.endsWith('_type')) continue
          if (typeof index[key] !== 'number') continue
          const column = row[key]
          if (!newRow._raw) newRow._raw = {}
          newRow._raw[index[key]] = column
          const imageHtml = imageCellHtml(row, key)
          const field = (this.fields || []).find((item) => item.columnUniqueTitle === key)
          const listed = displayListValue(column, field && field.multiselectDisplay)
          if (imageHtml != null) newRow[index[key]] = imageHtml
          else if (listed != null) newRow[index[key]] = listed
          else newRow[index[key]] = typeof column === 'string' ? formatAppInstant(column) : column
        }
        stampMapPoint(newRow, row, this.locationView)
        return newRow
      })
    },
    connectRealtimeWebSocket () {
      try {
        this.ws = new WebSocket(liveAppWebSocketUrl(VUE_APP_APP_SERVER_WS_URL, this.routeAccount, this.$route.params.application))
      } catch (err) {
        console.warn('Realtime WebSocket connection failed:', err)
        return
      }
      this.ws.onopen = () => {
        this.ws.send(JSON.stringify({
          type: 'realtime_subscribe',
          pageUniqueTitle: this.pageUniqueTitle,
          componentUniqueTitle: this.componentUniqueTitle,
          spreadSheetUniqueTitle: this.spreadSheetUniqueTitle,
          ...realtimeSubscribeFields({
            ...(this.$route && this.$route.params),
            account: this.routeAccount
          })
        }))
      }
      this.ws.onmessage = (event) => {
        let msg
        try { msg = JSON.parse(event.data) } catch { return }
        if (msg.type === 'realtime_update' &&
            msg.pageUniqueTitle === this.pageUniqueTitle &&
            msg.componentUniqueTitle === this.componentUniqueTitle) {
          if ((this.searchEnabled && this.searchTerm) || this.activePresetFilterIds.length) {
            this.fetchMapData(this.activeFilterEvent, { alwaysApply: true })
          } else {
            this.filteredItems = this.transformResponseData(msg.data)
            this.syncMapData(false)
          }
        }
      }
      this.ws.onclose = () => {
        setTimeout(() => {
          if (this.realtime && !this._isBeingDestroyed) this.connectRealtimeWebSocket()
        }, 3000)
      }
      this.ws.onerror = () => {}
    },
    disconnectRealtimeWebSocket () {
      if (!this.ws) return
      if (this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({
          type: 'realtime_unsubscribe',
          pageUniqueTitle: this.pageUniqueTitle,
          componentUniqueTitle: this.componentUniqueTitle
        }))
      }
      this.ws.onclose = null
      this.ws.close()
      this.ws = null
    }
  }
}
</script>

<style scoped>
.maps-container {
  padding: 1rem 0;
}

.map-canvas {
  height: 480px;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.map-note {
  margin-top: 0.5rem;
  font-size: 0.875rem;
}
</style>

<style>
.map-popup-field + .map-popup-field {
  margin-top: 0.4rem;
}

.map-popup-label {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6b7280;
}

.map-code-link {
  background: none;
  border: 0;
  padding: 0;
  color: #1d4ed8;
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  text-decoration: underline;
}
</style>
